// Jours ouvrés et jours fériés de France (métropole et Alsace-Moselle) — calcul PUR (serveur et navigateur), sans heure ni fuseau :
// les dates sont des chaînes AAAA-MM-JJ, manipulées à midi en temps universel (aucun décalage d'été ou d'hiver possible).
//
// Jours fériés retenus (Code du travail, art. L3133-1) : 1er janvier, lundi de Pâques, 1er mai, 8 mai, Ascension,
// lundi de Pentecôte, 14 juillet, 15 août, 1er novembre, 11 novembre, 25 décembre. Le lundi de Pentecôte est compté férié
// (journée de solidarité possible selon l'entreprise : non prise en compte ici).
// Alsace-Moselle (Bas-Rhin 67, Haut-Rhin 68, Moselle 57 ; Code du travail, art. L3134-13) : en plus, Vendredi saint et
// 26 décembre. Le régime d'un chantier se déduit du code postal du site (regimeDuSite).
// NON gérés : jours fériés propres aux départements et régions d'outre-mer.
// Jour ouvré = du lundi au vendredi, hors jour férié.

export interface JourFerie { date: string; nom: string }
export type Regime = 'metropole' | 'alsace_moselle';
export const LIBELLES_REGIME: Record<Regime, string> = { metropole: 'France métropolitaine', alsace_moselle: 'Alsace-Moselle' };

const RE_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const midi = (j: string) => new Date(`${j}T12:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
const deux = (n: number) => String(n).padStart(2, '0');

/** Date AAAA-MM-JJ réelle (refuse 2026-02-30, 2026-13-01…) entre 1900 et 2200. */
export function estDateValide(j: unknown): j is string {
  if (typeof j !== 'string') return false;
  const m = RE_DATE.exec(j);
  if (!m) return false;
  const a = Number(m[1]);
  if (a < 1900 || a > 2200) return false;
  const d = midi(j);
  return !Number.isNaN(d.getTime()) && iso(d) === j;
}

export function ajouterJoursCalendaires(j: string, n: number): string {
  const d = midi(j);
  d.setUTCDate(d.getUTCDate() + n);
  return iso(d);
}

/** Dimanche de Pâques (calendrier grégorien), algorithme de Meeus/Jones/Butcher. */
export function dimancheDePaques(annee: number): string {
  if (!Number.isInteger(annee) || annee < 1583 || annee > 4099) throw new RangeError(`Année hors limites : ${annee}`);
  const a = annee % 19;
  const b = Math.floor(annee / 100);
  const c = annee % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mois = Math.floor((h + l - 7 * m + 114) / 31);
  const jour = ((h + l - 7 * m + 114) % 31) + 1;
  return `${annee}-${deux(mois)}-${deux(jour)}`;
}

const cacheFeries = new Map<string, JourFerie[]>();

/** Les jours fériés d'une année (11 en métropole, 13 en Alsace-Moselle), dans l'ordre du calendrier. */
export function joursFeries(annee: number, regime: Regime = 'metropole'): JourFerie[] {
  const cle = `${annee}|${regime}`;
  const deja = cacheFeries.get(cle);
  if (deja) return deja;
  const paques = dimancheDePaques(annee);
  const a = String(annee);
  const liste: JourFerie[] = [
    { date: `${a}-01-01`, nom: 'Jour de l’an' },
    { date: ajouterJoursCalendaires(paques, 1), nom: 'Lundi de Pâques' },
    { date: `${a}-05-01`, nom: 'Fête du Travail' },
    { date: `${a}-05-08`, nom: 'Victoire 1945' },
    { date: ajouterJoursCalendaires(paques, 39), nom: 'Ascension' },
    { date: ajouterJoursCalendaires(paques, 50), nom: 'Lundi de Pentecôte' },
    { date: `${a}-07-14`, nom: 'Fête nationale' },
    { date: `${a}-08-15`, nom: 'Assomption' },
    { date: `${a}-11-01`, nom: 'Toussaint' },
    { date: `${a}-11-11`, nom: 'Armistice 1918' },
    { date: `${a}-12-25`, nom: 'Noël' },
    ...(regime === 'alsace_moselle' ? [
      { date: ajouterJoursCalendaires(paques, -2), nom: 'Vendredi saint (Alsace-Moselle)' },
      { date: `${a}-12-26`, nom: 'Saint-Étienne (Alsace-Moselle)' },
    ] : []),
  ].sort((x, y) => x.date.localeCompare(y.date));
  cacheFeries.set(cle, liste);
  return liste;
}

/** Nom du jour férié, ou null. */
export function ferie(j: string, regime: Regime = 'metropole'): string | null {
  return joursFeries(Number(j.slice(0, 4)), regime).find((f) => f.date === j)?.nom ?? null;
}

export const estWeekEnd = (j: string) => [0, 6].includes(midi(j).getUTCDay());
export const estOuvre = (j: string, regime: Regime = 'metropole') => !estWeekEnd(j) && ferie(j, regime) === null;

/**
 * Régime des jours fériés d'un chantier, d'après le code postal écrit dans l'adresse ou la ville du site (les sites n'ont pas
 * de champ « code postal » à part). 57xxx, 67xxx, 68xxx = Alsace-Moselle ; un autre code = métropole ; aucun code trouvé =
 * métropole, et l'écran le dit (rien n'est deviné à partir du nom de la ville).
 */
export function regimeDuSite(adresse: string | null | undefined, ville: string | null | undefined): { regime: Regime; code_postal: string | null; motif: string } {
  const codes = `${adresse ?? ''} ${ville ?? ''}`.match(/\b(?:0[1-9]|[1-8]\d|9[0-8])\d{3}\b/g) ?? [];
  const code = codes.at(-1) ?? null;
  if (!code) return { regime: 'metropole', code_postal: null, motif: 'Aucun code postal dans l’adresse du site : jours fériés de France métropolitaine.' };
  const alsace = /^(57|67|68)/.test(code);
  return { regime: alsace ? 'alsace_moselle' : 'metropole', code_postal: code,
    motif: alsace ? `Site en Alsace-Moselle (code postal ${code}) : Vendredi saint et 26 décembre comptés fériés.` : `Site en France métropolitaine (code postal ${code}).` };
}

/** Plafond de jours ouvrés d'un délai (sécurité de la boucle ; un devis ne dépasse pas deux ans de travaux). */
export const JOURS_OUVRES_MAX = 520;

/**
 * Date de fin = début + N jours ouvrés. Le jour de début compte s'il est ouvré ; sinon le travail commence le premier
 * jour ouvré suivant (`debut_effectif`). `feries` : jours fériés tombant un jour de semaine entre le début saisi et la fin
 * (ceux qui ont réellement décalé la fin) ; un férié tombant un samedi ou un dimanche ne change rien et n'est pas listé.
 */
export function finApresJoursOuvres(debut: string, jours: number, regime: Regime = 'metropole'): { debut_effectif: string; fin: string; feries: JourFerie[] } {
  if (!estDateValide(debut)) throw new RangeError(`Date de début invalide : ${String(debut)}`);
  if (!Number.isInteger(jours) || jours < 1 || jours > JOURS_OUVRES_MAX) throw new RangeError(`Nombre de jours ouvrés hors limites : ${jours}`);
  const feries: JourFerie[] = [];
  let j = debut;
  let compte = 0;
  let premier: string | null = null;
  for (;;) {
    const nom = estWeekEnd(j) ? null : ferie(j, regime);
    if (nom) feries.push({ date: j, nom });
    else if (!estWeekEnd(j)) {
      compte++;
      premier ??= j;
      if (compte === jours) return { debut_effectif: premier, fin: j, feries };
    }
    j = ajouterJoursCalendaires(j, 1);
  }
}

/** « 06/04/2026 » */
export function dateFrCourte(j: string): string {
  const m = RE_DATE.exec(j);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : j;
}
